import { Option } from 'models/Global';
import {
  AddMembersPeopleTableKeys,
  REQUIRED_COLUMNS,
  TABLE_COLUMNS,
} from './data';
import { SheetEnterpriseMember } from 'models/Enterprises';
import {
  addMissingRequiredFields,
  checkIsValidEmail,
  checkIsValidName,
  getArrayLength,
  isLinkedinUrl,
} from 'utils';
import { getIsValidPhone, getPhone } from 'phoneNumberUtils';

export const checkIfColumnIsValid = (column: string) =>
  TABLE_COLUMNS.includes(column as AddMembersPeopleTableKeys);

export const checkHasEveryRequiredColumn = (columns: string[]) =>
  REQUIRED_COLUMNS.every((col) => columns.includes(col));

export const addMissingRequiredFieldsToMember = (
  member: SheetEnterpriseMember,
) =>
  addMissingRequiredFields(REQUIRED_COLUMNS, member) as SheetEnterpriseMember;

export const findEnterpriseArrayOption = (el: string, arr: Option[] = []) =>
  arr.find(({ label, value }) => el === label || el === value);

export const findEnterpriseArrayLabel = (el: string, arr: Option[] = []) =>
  findEnterpriseArrayOption(el, arr)?.label;

export const findEnterpriseArrayValue = (el: string, arr: Option[] = []) =>
  findEnterpriseArrayOption(el, arr)?.value;

export const isInvalidEnterpriseArrayValue = (el: string, arr: Option[]) =>
  !(el && findEnterpriseArrayValue(el, arr));

export const checkIsMemberInvalid = (
  member: SheetEnterpriseMember,
  units: Option[],
  memberSeniorities: Option[],
  locations: Option[],
) => {
  const {
    email,
    firstName,
    lastName,
    title,
    seniority,
    businessUnitName,
    location,
  } = member;

  return (
    !title ||
    !checkIsValidEmail(email) ||
    !checkIsValidName(firstName) ||
    !checkIsValidName(lastName) ||
    isInvalidEnterpriseArrayValue(businessUnitName, units) ||
    isInvalidEnterpriseArrayValue(seniority, memberSeniorities) ||
    isInvalidEnterpriseArrayValue(location, locations)
  );
};

export const checkAreAllRequiredFieldsValid = (
  members: SheetEnterpriseMember[],
  units: Option[],
  memberSeniorities: Option[],
  locations: Option[],
) =>
  !members.some((member) =>
    checkIsMemberInvalid(member, units, memberSeniorities, locations),
  );

export const getInvalidMembersCount = (
  members: SheetEnterpriseMember[],
  units: Option[],
  memberSeniorities: Option[],
  locations: Option[],
) =>
  getArrayLength(
    members.filter((member) =>
      checkIsMemberInvalid(member, units, memberSeniorities, locations),
    ),
  );

export const checkIsPhoneNumberValid = (phoneNumber?: string | number | null) =>
  !!phoneNumber && getIsValidPhone(getPhone(String(phoneNumber)));

export const checkIsLinkedinAccountValid = (linkedinAccount?: string | null) =>
  !!linkedinAccount && isLinkedinUrl(String(linkedinAccount));

export const checkHasInvalidOptionalValues = (
  member: SheetEnterpriseMember,
) => {
  const { phoneNumber, linkedinAccount } = member;

  const hasInvalidPhone =
    !!phoneNumber && !checkIsPhoneNumberValid(phoneNumber);
  const hasInvalidLinkedin =
    !!linkedinAccount && !checkIsLinkedinAccountValid(linkedinAccount);

  return hasInvalidPhone || hasInvalidLinkedin;
};

export const getInvalidOptionalMembersCount = (
  members: SheetEnterpriseMember[],
) => getArrayLength(members.filter(checkHasInvalidOptionalValues));

export const prepareValuesForApi = (members: SheetEnterpriseMember[]) =>
  members.map(({ _id, isOfficeManager, ...rest }) => {
    const { phoneNumber, linkedinAccount, ...restOfColumns } =
      Object.fromEntries(
        Object.entries(rest).filter(([key]) => checkIfColumnIsValid(key)),
      );

    return {
      ...restOfColumns,
      ...(checkIsPhoneNumberValid(phoneNumber)
        ? {
            phoneNumber: String(phoneNumber),
          }
        : {}),
      ...(checkIsLinkedinAccountValid(linkedinAccount)
        ? {
            linkedinAccount: String(linkedinAccount),
          }
        : {}),
      isOfficeManager: !!isOfficeManager,
    };
  });
